import { ASSET_URLS, BASE_STYLE, resolveUrls } from '@/lib/templates/template-loader'
import type { TemplateFiles } from '@/lib/templates/template-files'
import type { Theme, ThemeToken } from '@/lib/templates/template-theme'

/** One page of a design: its slide element and the markers the layout carries. */
export interface DesignPage {
  page: number
  layout: string
  unit: number | null
  eid: string
  element: HTMLElement
}

/** A design file, parsed once: its template styles, its root without slides and its pages in order. */
export interface DesignDocument {
  templateId: string
  name: string
  styles: string
  /** The text of each `<style>` element, in file order. */
  styleBlocks: readonly string[]
  root: HTMLElement
  pages: readonly DesignPage[]
  theme: Theme
  fontFamilies: readonly string[]
  /** Each object URL the document holds, to the path it was resolved from. */
  originals: ReadonlyMap<string, string>
}

/** A design file with its paths resolved to object URLs, and the path each URL stands for. */
export interface ResolvedDesignHtml {
  html: string
  originals: ReadonlyMap<string, string>
}

const RELATIVE = '(?!\\/|data:|blob:|https?:|#)'
const RELATIVE_URLS = [
  new RegExp(`(?<=\\bsrc=")${RELATIVE}([^"]+)(?=")`, 'g'),
  new RegExp(`(?<=\\bsrc=')${RELATIVE}([^']+)(?=')`, 'g'),
  new RegExp(`(?<=\\burl\\(\\s*['"]?)${RELATIVE}([^'")\\s]+)`, 'g'),
]
const TEMPLATE_CLASS = /^t\d{2}$/
const THEME_PREFIX = '--theme-'
const COUNTER = /\d+(\s*\/\s*)\d+/
const COUNTER_WIDTH = 2

function integerOrNull(value: string | undefined): number | null {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isNaN(parsed) ? null : parsed
}

/** The inline `--theme-*` tokens of a design root. */
export function themeOf(root: HTMLElement): Theme {
  const theme: Record<string, string> = {}
  for (const property of Array.from(root.style))
    if (property.startsWith(THEME_PREFIX))
      theme[property] = root.style.getPropertyValue(property).trim()
  return theme as Partial<Record<ThemeToken, string>>
}

/** Parses a design file without its scripts; throws when it has no contract 2 root or no page. */
export function parseDesignDocument(
  html: string,
  shippedFamilies: readonly string[],
  originals: ReadonlyMap<string, string> = new Map(),
): DesignDocument {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  for (const script of parsed.querySelectorAll('script')) script.remove()
  const root = parsed.querySelector<HTMLElement>('[data-contract="2"]')
  if (!root) throw new Error('design file has no contract 2 root')
  const slides = Array.from(root.querySelectorAll<HTMLElement>('.slide[data-page]'))
  if (slides.length === 0) throw new Error('design file has no pages')
  const styledText = [
    ...Array.from(parsed.querySelectorAll('style'), (style) => style.textContent ?? ''),
    ...Array.from(parsed.querySelectorAll('[style]'), (el) => el.getAttribute('style') ?? ''),
  ].join('\n')
  const pages = slides
    .map((element) => ({
      page: integerOrNull(element.dataset.page) ?? 0,
      layout: element.dataset.layout ?? '',
      unit: integerOrNull(element.dataset.unit),
      eid: element.dataset.eid ?? '',
      element,
    }))
    .sort((a, b) => a.page - b.page)
  for (const slide of slides) slide.remove()
  const styleBlocks = Array.from(parsed.querySelectorAll('style'), (style) => style.textContent)
  return {
    templateId: Array.from(root.classList).find((name) => TEMPLATE_CLASS.test(name)) ?? '',
    name: root.dataset.name ?? '',
    styles: styleBlocks.join('\n'),
    styleBlocks,
    root,
    pages,
    theme: themeOf(root),
    fontFamilies: shippedFamilies.filter(
      (family) => styledText.includes(`"${family}"`) || styledText.includes(`'${family}'`),
    ),
    originals,
  }
}

function recording(
  files: TemplateFiles,
  originalOf: (path: string) => string,
  into: Map<string, string>,
): TemplateFiles {
  return {
    readText: (path) => files.readText(path),
    async readObjectUrl(path) {
      const url = await files.readObjectUrl(path)
      into.set(url, originalOf(path))
      return url
    },
  }
}

function bundleOriginal(path: string): string {
  return path.startsWith('assets/') ? `/__templates/${path}` : `/__${path}`
}

/** `html` with the template and font URLs resolved through `bundle` and the relative paths through `design`, and the path behind every URL. */
export async function resolveDesignHtml(
  html: string,
  design: TemplateFiles,
  bundle: TemplateFiles,
  originals: Map<string, string> = new Map(),
): Promise<ResolvedDesignHtml> {
  const [bundled] = await resolveUrls(
    [html],
    ASSET_URLS,
    recording(bundle, bundleOriginal, originals),
  )
  const [resolved] = await resolveUrls(
    [bundled ?? html],
    RELATIVE_URLS,
    recording(design, (path) => path, originals),
  )
  return { html: resolved ?? html, originals }
}

function restoreOriginals(markup: string, originals: ReadonlyMap<string, string>): string {
  let out = markup
  for (const [url, original] of originals) out = out.split(url).join(original)
  return out
}

function renumberCounter(text: string, page: number, total: number): string {
  const pad = (value: number) => String(value).padStart(COUNTER_WIDTH, '0')
  return text.replace(
    COUNTER,
    (_whole, separator: string) => `${pad(page)}${separator}${pad(total)}`,
  )
}

/** The design as a file: its style blocks and its root with every page in order, pages renumbered, editing marks removed and object URLs turned back into paths. */
export function serialiseDesign(doc: DesignDocument): string {
  const root = doc.root.cloneNode(true) as HTMLElement
  const total = doc.pages.length
  doc.pages.forEach((entry, index) => {
    const slide = entry.element.cloneNode(true) as HTMLElement
    slide.dataset.page = String(index + 1)
    for (const marked of [slide, ...slide.querySelectorAll('[contenteditable]')])
      marked.removeAttribute('contenteditable')
    for (const counter of slide.querySelectorAll('[data-slot="pageCounter"]'))
      counter.textContent = renumberCounter(counter.textContent ?? '', index + 1, total)
    root.append(slide)
  })
  const markup = [...doc.styleBlocks.map((block) => `<style>${block}</style>`), root.outerHTML]
  return restoreOriginals(markup.join('\n'), doc.originals)
}

/** One 1080 x 1350 page of `doc` as a whole HTML document; throws when the page does not exist. */
export function buildDesignPageDocument(
  doc: DesignDocument,
  page: number,
  fontCss: string,
): string {
  const source = doc.pages.find((candidate) => candidate.page === page)
  if (!source) throw new Error(`design has no page ${page}`)
  const holder = doc.root.cloneNode(true) as HTMLElement
  holder.append(source.element.cloneNode(true))
  return `<!doctype html><html><head><meta charset="utf-8"><style data-font-faces>${fontCss}</style><style>${doc.styles}</style><style>${BASE_STYLE}</style></head><body>${holder.outerHTML}</body></html>`
}
